import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Crown, LogIn, Sparkles, Package } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useCommercialConfig } from "@/lib/pricing-labels";
import { planCopyLang, quotaLimitCopy, type QuotaCopyKind } from "@/lib/plan-copy";

type Tier = "anon" | "free" | "premium";
type Reason = "anon_limit" | "free_limit" | "premium_limit" | "premium_monthly_limit";

interface Props {
  open: boolean;
  tier: Tier;
  reason?: Reason;
  resetInMinutes: number;
  onClose: () => void;
  onOpenPremium: () => void;
  lang: string;
}

type CtaCopy = { cta1: string; cta2: string; reset: string };

const CTA: Record<string, CtaCopy> = {
  pt: { cta1: "Comprar pack", cta2: "Ver premium", reset: "Reseta em" },
  en: { cta1: "Buy a pack", cta2: "See premium", reset: "Resets in" },
  es: { cta1: "Comprar pack", cta2: "Ver premium", reset: "Se reinicia en" },
  fr: { cta1: "Acheter un pack", cta2: "Voir premium", reset: "Réinitialisation dans" },
  it: { cta1: "Acquistare pacchetto", cta2: "Vedi premium", reset: "Si azzera tra" },
  de: { cta1: "Paket kaufen", cta2: "Premium ansehen", reset: "Zurücksetzen in" },
};

const SIGNIN_CTA: Record<string, string> = {
  pt: "Entrar / Registar",
  en: "Sign in / Sign up",
  es: "Entrar / Registrarse",
  fr: "Se connecter / S'inscrire",
  it: "Accedi / Registrati",
  de: "Anmelden / Registrieren",
};

const CLOSE_CTA: Record<string, string> = {
  pt: "Fechar", en: "Close", es: "Cerrar", fr: "Fermer", it: "Chiudi", de: "Schließen",
};

function formatReset(minutes: number, lang: string): string {
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 24 * 60) return `${Math.round(minutes / 60)}h`;
  const days = Math.round(minutes / (60 * 24));
  return lang === "pt" ? `${days} dia${days === 1 ? "" : "s"}` : `${days} day${days === 1 ? "" : "s"}`;
}


export function QuotaLimitModal({ open, tier, reason, resetInMinutes, onClose, onOpenPremium, lang }: Props) {
  const navigate = useNavigate();
  // Os números vêm SEMPRE da configuração confirmada pelo servidor, nunca de
  // texto fixo: alterar os limites no Painel do Fundador muda esta mensagem.
  const { limits } = useCommercialConfig();
  const key: QuotaCopyKind =
    reason === "premium_monthly_limit"
      ? "premium_monthly_limit"
      : tier === "anon"
      ? "anon_limit"
      : tier === "free"
      ? "free_limit"
      : "premium_limit";
  const copy = quotaLimitCopy(key, limits, lang);
  const cta = CTA[planCopyLang(lang)] ?? CTA.en;
  const isPremiumLimit = tier === "premium";

  function handlePrimary() {
    if (tier === "anon") {
      onClose();
      navigate({ to: "/login" });
      return;
    }
    // free + premium → comprar pack
    onClose();
    navigate({ to: "/credits" });
  }

  function handleSecondary() {
    onClose();
    if (isPremiumLimit) return; // "Fechar"
    onOpenPremium();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            {copy.title}
          </DialogTitle>
          <DialogDescription className="pt-2">{copy.desc}</DialogDescription>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">{cta.reset}: {formatReset(resetInMinutes, lang)}</p>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={handleSecondary} className="gap-2">
            {isPremiumLimit ? null : <Crown className="w-4 h-4" />}
            {isPremiumLimit ? (CLOSE_CTA[planCopyLang(lang)] ?? CLOSE_CTA.en) : cta.cta2}
          </Button>
          <Button onClick={handlePrimary} className="gap-2">
            {tier === "anon" ? <LogIn className="w-4 h-4" /> : <Package className="w-4 h-4" />}
            {tier === "anon" ? (SIGNIN_CTA[planCopyLang(lang)] ?? SIGNIN_CTA.en) : cta.cta1}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
